/**
 * API istemcisi.
 *
 * Mutlak URL YOK: SPA uretimde API ile AYNI ORIGIN'den servis edilir, gelistirmede
 * Vite `/api`'yi proxy'ler. Bu sayede oturum cerezi hicbir yerde CORS'a takilmaz.
 */

export class ApiHata extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * Sunucunun makine okunur hata kodu (varsa). Ekran bir hatayi TANIMAK
     * zorundaysa MESAJI ESLESTIRMEZ — metin bir gun degisir ve dal sessizce
     * olur. Ornek: `BASKA_SANTIYEDE_KAYITLI`.
     */
    readonly kod?: string,
  ) {
    super(message);
  }
}

/**
 * ÖNBELLEKTEN OKUMA GÖSTERGESİ.
 *
 * Service worker çevrimdışında son bilinen cevabı döner ve `X-Mod-Onbellek`
 * başlığı koyar. Kullanıcı TAZE VERİ SANDIĞI eski bir listeye bakmamalı —
 * "sessiz kayıp yok" ilkesi okumalar için de geçerli.
 */
let onbellekten = false;
const dinleyiciler = new Set<(v: boolean) => void>();

export function onbellektenMi(): boolean {
  return onbellekten;
}

export function onbellekDinle(f: (v: boolean) => void): () => void {
  dinleyiciler.add(f);
  return () => dinleyiciler.delete(f);
}

function onbellekBildir(v: boolean) {
  if (onbellekten === v) return;
  onbellekten = v;
  for (const f of dinleyiciler) f(v);
}

async function istek<T>(yol: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`/api${yol}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'same-origin',
  });

  // Yalnız okumalar önbelleklenir; yazma cevabı hiçbir zaman önbellekten gelmez.
  if (!init?.method || init.method === 'GET') {
    onbellekBildir(r.headers.get('X-Mod-Onbellek') === '1');
  }

  const govde = await r.json().catch(() => null);
  if (!r.ok) {
    // Sunucu mesaji @mod/domain kurallarindan gelir — client kendi metnini uydurmaz.
    const mesaj =
      (govde as { hata?: string; message?: string } | null)?.hata ??
      (govde as { message?: string } | null)?.message ??
      'Beklenmeyen bir hata oluştu';
    throw new ApiHata(mesaj, r.status, (govde as { kod?: string } | null)?.kod);
  }
  return govde as T;
}

/**
 * DOSYA İNDİRME (Excel/PDF dışa aktarımı).
 *
 * ⚠️ `window.location = …` ile GİTMİYORUZ. O yol hata gövdesini okuyamaz:
 *    sunucu 403 dönerse tarayıcı sessizce bir şey yapmaz ya da JSON'u sekmede
 *    açar; kullanıcı "indirme çalışmıyor" der ve nedeni göremez. Blob yolunda
 *    hata normal `ApiHata` olarak yüzeye çıkar.
 *
 * Dosya adı sunucudaki `Content-Disposition`'dan okunur — ad tek yerde üretilsin.
 */
async function indir(yol: string): Promise<void> {
  const r = await fetch(`/api${yol}`, { credentials: 'same-origin' });
  if (!r.ok) {
    const govde = (await r.json().catch(() => null)) as { hata?: string; message?: string } | null;
    throw new ApiHata(govde?.hata ?? govde?.message ?? 'Dosya oluşturulamadı', r.status);
  }

  const bilgi = r.headers.get('Content-Disposition') ?? '';
  const ad = /filename="?([^"]+)"?/.exec(bilgi)?.[1] ?? 'mod-erp-disa-aktarim';

  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ad;
  document.body.append(a);
  a.click();
  a.remove();
  // Bellek sızıntısı olmasın; tarayıcı indirmeyi başlattıktan sonra serbest.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const api = {
  get: <T>(yol: string) => istek<T>(yol),
  post: <T>(yol: string, govde?: unknown) =>
    istek<T>(yol, { method: 'POST', body: JSON.stringify(govde ?? {}) }),
  patch: <T>(yol: string, govde?: unknown) =>
    istek<T>(yol, { method: 'PATCH', body: JSON.stringify(govde ?? {}) }),
  indir,
};

/**
 * SAHA KOMUTU — cevrimdisiysa kuyruga alir, cevrimiciyse dogrudan gonderir.
 *
 * Cevrimdisi basarisizlik KULLANICIYA HATA OLARAK GOSTERILMEZ: sahadaki adam
 * tikini atmistir, isi bitmistir. Komut kuyrukta durur ve baglanti gelince
 * gider; ekranin ustundeki gosterge neyin beklediğini soyler.
 */
export async function sahaKomutu(
  yol: string,
  govde: Record<string, unknown>,
): Promise<{ kuyruga: boolean }> {
  const { komutKuyrugaAl } = await import('./outbox');

  if (!navigator.onLine) {
    await komutKuyrugaAl(yol, govde);
    return { kuyruga: true };
  }
  try {
    await api.post(yol, govde);
    return { kuyruga: false };
  } catch (e) {
    // Kural ihlali (4xx) kuyruğa ALINMAZ — tekrar denemek düzeltmez, kullanıcı görmeli.
    if (e instanceof ApiHata && e.status >= 400 && e.status < 500) throw e;
    await komutKuyrugaAl(yol, govde);
    return { kuyruga: true };
  }
}

import type { Role } from '@mod/domain';
/** Rol listesi TEK KAYNAK: @mod/domain. Sema, kural ve arayuz ayni listeyi kullanir. */
export type Rol = Role;
export type SistemRol = 'admin' | 'kullanici';

/** Ray'daki müşteri seçicisinin bir satırı. */
export interface Santiye {
  siteId: string;
  siteAd: string;
  konum: string | null;
  musteriId: string;
  musteriAd: string;
  /** Logo bu kodla eşlenir — bkz. src/marka. */
  musteriKod: string;
  /**
   * İş emri SAP siparişi mi (SOCAR) — "Sorumlu İşyeri" ve "Planlama Grubu"
   * yalnız bu açıkken sorulur. Eski oturum çerezinde olmayabilir → false say.
   */
  sapAlanlari?: boolean;
  /** Hakediş verisi tutulur mu (SOCAR) — söküm talep tarihi, hakediş metrajı, kesinti. */
  hakedisVerisi?: boolean;
}

export interface Ben {
  id: string;
  ad: string;
  telefon: string;
  /** Profil alanı — girişte kullanılmaz, boş olabilir. */
  email: string | null;
  rol: Rol;
  sistemRol: SistemRol;
  testKullanicisi: boolean;
  sefYetkisi: boolean;
  /** Müşteri tarafındaki izleyici — menü dört sayfaya iner, ekranlar salt okunur. */
  musteri: boolean;
  temsil: { gercekAd: string; gercekId: string } | null;
  /** Aktif müşteri — başlıkta ve raporda görünür. */
  santiye: Santiye;
  /**
   * Geçiş yapılabilecek müşteriler. TEK elemanlıysa seçici çizilmez:
   * saha çalışanı tek müşteride çalışır.
   */
  santiyeler: Santiye[];
}

export interface KullaniciSatir {
  id: string;
  ad: string;
  telefon: string;
  email: string | null;
  sicilNo: number | null;
  rol: Rol | null;
  sistemRol: SistemRol;
  aktif: boolean;
  testKullanicisi: boolean;
  /** Müşteri: ekip başılığı ROL değil, Usta'ya verilen YETKİ. */
  ekipBasiYetkisi: boolean;
  sonGirisAt: string | null;
  kilitliAt: string | null;
}

export interface TemsilAdayi {
  id: string;
  ad: string;
  rol: Rol | null;
  sicilNo: number | null;
  testKullanicisi: boolean;
  sistem: boolean;
}

/** Rol etiketleri @mod/domain'den — arayüz ve mail aynı adı kullansın. */
export { ROL_ETIKET } from '@mod/domain';
